import type { FC } from 'react';
import { Card, Stack, styled, TextField, Typography } from '@mui/material';
import { Button, Iconify, Modal, ModalInner } from '@pflegenavi/web-components';
import { DirectDebitStatus } from '@pflegenavi/shared/api';
import { useTranslation } from 'react-i18next';

interface CancelDirectDebitViewProps {
  status: DirectDebitStatus;
  onCancelClick: () => void;
}

export const CancelDirectDebitView: FC<CancelDirectDebitViewProps> = ({
  status,
  onCancelClick,
}) => {
  const { t } = useTranslation();
  if (status === DirectDebitStatus.Inactive) {
    return null;
  }

  const renderCancelContent = () => {
    if (status === DirectDebitStatus.Pending) {
      return (
        <>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t('mobile.family-member.direct-debit-being-setup')}
          </Typography>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t('mobile.family-member.direct-debit-setup-will-inform-you')}
          </Typography>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t('mobile.family-member.direct-debit-want-to-cancel')}
          </Typography>
        </>
      );
    }

    return (
      <>
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{
            fontWeight: 600,
          }}
        >
          {t('mobile.family-member.direct-debit-ready-to-use')}
        </Typography>
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{
            fontWeight: 600,
          }}
        >
          {t('mobile.family-member.direct-debit-want-to-cancel')}
        </Typography>
      </>
    );
  };

  return (
    <RootContainer>
      <Typography
        gutterBottom
        sx={{
          fontWeight: 700,
          ml: 1,
        }}
      >
        {t('mobile.family-member.status-update')}
      </Typography>
      <ContentContainer alignItems="stretch" spacing={2}>
        {renderCancelContent()}

        <Stack
          sx={{
            alignItems: 'flex-end',
          }}
        >
          <Button
            onClick={onCancelClick}
            variant="contained"
            color="error"
            endIcon={<Iconify icon="bx:x" color="white" />}
          >
            {t('direct-debit.cancel')}
          </Button>
        </Stack>
      </ContentContainer>
    </RootContainer>
  );
};

interface CancelModalViewProps {
  open: boolean;
  handleClose: () => void;
  message: string;
  onMessageChange: (value: string) => void;
  onSubmit: () => void;
  isPending: boolean;
  isSuccess: boolean;
}

export const CancelModalView: FC<CancelModalViewProps> = ({
  open,
  handleClose,
  message,
  onMessageChange,
  onSubmit,
  isPending,
  isSuccess,
}) => {
  const { t } = useTranslation();
  return (
    <Modal open={open} handleClose={handleClose}>
      <ModalInner>
        <CancelModalContainer>
          {!isSuccess ? (
            <>
              <Typography
                color="primary"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                {t('direct-debit.cancellation-request')}
              </Typography>
              <Stack>
                <Typography
                  sx={{
                    color: 'grey.800',
                    fontSize: 16,
                    mb: 0.2,
                  }}
                >
                  {t('direct-debit.reason-of-cancellation')}
                </Typography>
                <TextField
                  value={message}
                  onChange={(e) => onMessageChange(e.target.value)}
                  placeholder={t('direct-debit.cancel-eg-reason')}
                  multiline
                  rows={4}
                />
              </Stack>
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'flex-end',
                  gap: 1,
                  mt: 2,
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClose}
                >
                  {t('direct-debit.I-do-not-want-to-cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  loading={isPending}
                  onClick={onSubmit}
                >
                  {t('direct-debit.submit-cancellation-request')}
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography
                color="success"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                {t('direct-debit.successfully-cancelled-request')}
              </Typography>
              <Typography
                color="info"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                {t('direct-debit.nursing-home-will-contact-you')}
              </Typography>
            </>
          )}
        </CancelModalContainer>
      </ModalInner>
    </Modal>
  );
};

const RootContainer = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    p: 1,
    pt: 2,
    width: '100%',
    borderRadius: 3,
  })
);

const ContentContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    bgcolor: 'grey.200',
    paddingX: 2,
    paddingY: 3,
    pb: 2,
    borderRadius: 3,
  })
);

const CancelModalContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    width: 450,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 2,
    borderRadius: 2,
  })
);
